import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BaseHttpService } from './base.http.service';
import { ICurrentRebalance, IPortfolio, IPortfolioStatus, ITransferCashCommand } from 'src/app/models/portfolios';
import { AppConfig } from 'src/app/app.config';
import {IPortfolioNetAsset, IPortfolioNetAssetValueResponse} from "../../models/portfolioNetAsset";
import {BehaviorSubject, Observable, of} from "rxjs";
import { catchError, map } from 'rxjs/operators';
import { IEnvironmentConfig } from 'src/app/models/environmentConfig';

export interface ISearchPortfoliosSubscribers{
    query: string;
    excludeAllPortfoliosSubscribedToAModel?: boolean;
    excludePortfoliosSubscribedToModelPortfolioIds?: number;
    limit: number;
}

@Injectable()
export class PortfoliosService extends BaseHttpService {
    constructor(private http: HttpClient, private appConfig: AppConfig) {
        super();
    }

    get envSettings(): IEnvironmentConfig {
        return this.appConfig.getEnvironmentConfig();
    }

    public getInvestorPortfolios(entitySubTypes: number[] = []) : Observable<IPortfolio[]> {

        let url = `${this.envSettings.webApiUrl}portfolio`;

        if (entitySubTypes.length > 0){
            url += "?entitySubTypes=" + entitySubTypes.map(x => x).join("&entitySubTypes=");
        }

        return this.http.get<IPortfolio[]>(url, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }

    public searchPortfolios(query: string) {

        let url;
        if (query !== '') {
            url = `${this.envSettings.webApiUrl}admin/portfolios/search?query=${query}`;
        } else {
            url = `${this.envSettings.webApiUrl}admin/portfolios/search`;
        }
        return this.http.get(url, {
            observe: 'body',
            responseType: 'json',
            headers: this.getHttpHeaders()
        });
    }

    public searchPortfoliosSubscribers(dataPrams: ISearchPortfoliosSubscribers) {
        let url = new URL(`${this.envSettings.webApiUrl}admin/portfolios/search`);
        url.searchParams.append('limit', dataPrams.limit.toString());

        if (dataPrams.excludeAllPortfoliosSubscribedToAModel)
          url.searchParams.append('excludeAllPortfoliosSubscribedToAModel', dataPrams.excludeAllPortfoliosSubscribedToAModel.toString());

        if (dataPrams.excludePortfoliosSubscribedToModelPortfolioIds)
          url.searchParams.append('excludePortfoliosSubscribedToModelPortfolioIds', dataPrams.excludePortfoliosSubscribedToModelPortfolioIds.toString());

        if(dataPrams.query)
          url.searchParams.append('query', dataPrams.query);

      return this.http.get(url.toString(), {
            observe: 'body',
            responseType: 'json',
            headers: this.getHttpHeaders()
        });
    }

    public loadPortfolioEntity(portfolioId: number) {
        const date = this.parseDate(new Date());
        const url = `${this.envSettings.webApiUrl}portfolio/${portfolioId}/allpositions/${date}`;
        return this.http.get(url, {
            observe: 'body',
            responseType: 'json',
            headers: this.getHttpHeaders()
        });
    }

    public loadPortfoliosForEntities(entityIds: number[] = []) {
        const url = new URL(`${this.envSettings.webApiUrl}portfolios`);

        if(!!entityIds && entityIds.length > 0)
        entityIds.forEach((entityId) => {
          url.searchParams.append('entityIds', entityId.toString());
        });

        return this.http.get(url.toString(), {
            observe: 'body',
            responseType: 'json',
            headers: this.getHttpHeaders()
        });
    }

    private _portfolioNetAssetResponse$: BehaviorSubject<IPortfolioNetAssetValueResponse> =
    new BehaviorSubject<IPortfolioNetAssetValueResponse>({
        data: [],
        error: false,
        errorMessage: ''
    });

    public portfolioNetAssetResponse$: Observable<IPortfolioNetAssetValueResponse> = this._portfolioNetAssetResponse$.asObservable();

    public getPortfoliosNetAssets(portfolioIds: number[], date: Date): void {
        const url = new URL(`${this.envSettings.webApiUrl}portfolios/netassetvalue`);

        // Set url params
        url.searchParams.set('date', this.parseDate(date));

        portfolioIds.forEach((portfolioId) => {
            url.searchParams.append('portfolioIds', portfolioId.toString());
        });

        this.http.get<IPortfolioNetAsset[]>(url.toString(), {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        })
        .pipe(
            map((portfolioNetAssetValueResponse) => {
            return {
                data: portfolioNetAssetValueResponse,
                error: false,
                errorMessage: ''
            } as IPortfolioNetAssetValueResponse;
            }),
            catchError(() => {
                return of({
                data: [],
                error: true,
                errorMessage: 'Unable to load portfolio net asset value data.'
                });
            })
        )
        .subscribe(response => {
            this._portfolioNetAssetResponse$.next(response);
        });
    }

    public cancelRebalance(portfolioId: number, currentRebalance: ICurrentRebalance){
        const url = `${this.envSettings.webApiUrl}portfolio/${portfolioId}/instructions/${currentRebalance?.instructionId}/cancel`;
        return this.http.post(url, "", {
            responseType: 'json',
            headers: this.getHttpHeaders()
        });
    }

    //
    // transfer cash call
    public transferCash(transferCashCommand: ITransferCashCommand)
    {
        // build our json payload
        const jsonBody = {
            DestinationPortfolioId: transferCashCommand.destPortfolioId,
            Date: transferCashCommand.date,
            Amount: transferCashCommand.amount,
            Currency: transferCashCommand.currency
        }

        // build our url
        const url = `${this.envSettings.webApiUrl}portfolio/${transferCashCommand.srcPortfolioId}/cashTransfers`;

        // make call and get response
        return this.http.post(url, jsonBody, {
            responseType: 'json',
            headers: this.getHttpHeaders()
        });
    }

    public getPortfolioStatus(portfolioId: number){
        let url = `${this.envSettings.webApiUrl}portfolio/${portfolioId}/status`;

        return this.http.get<IPortfolioStatus>(
            url.toString(),
            {
                observe: 'body',
                responseType: 'json',
                headers: this.getHttpHeaders()
            }
        );
    }

    public getPortfolio(portfolioId: number): Observable<IPortfolio> {
      return this.http.get<IPortfolio>(
        `${this.envSettings.webApiUrl}portfolio/${portfolioId}`,
        {
          observe: 'body',
          responseType: 'json',
          headers: this.getHttpHeaders()
        }
      );
    }
}